import type { AuthenticationResult } from '@azure/msal-browser';
import { PublicClientApplication } from '@azure/msal-browser';

import { customAPITokenRequest, loginRequest, msalConfig } from '../shared/config/authConfig';

interface CachedMsalToken {
    halfExpiryTime: number;
    authenticationResult: AuthenticationResult;
}

interface CachedMsalTokenState {
    loginTokenCache: CachedMsalToken;
    customTokenCache: CachedMsalToken;
}

const msalInstance = new PublicClientApplication(msalConfig());
const DefaultMsalTokenTimeout = 5 * 60 * 1000;

let globalTokenState: CachedMsalTokenState | undefined;

const isGlobalTokenStateValid = (): boolean => {
    const now = Date.now();
    return (
        !!globalTokenState &&
        now < globalTokenState.loginTokenCache.halfExpiryTime &&
        now < globalTokenState.customTokenCache.halfExpiryTime
    );
};

/**
 * it's good practice to refresh token before it expires, use half of the remaining time as buffer
 */
const getHalfExpiryTime = (tokenGenerationTime: number, expiresOn: Date | null) => {
    if (!expiresOn) {
        return tokenGenerationTime + DefaultMsalTokenTimeout;
    }

    const buffer = (expiresOn.getTime() - tokenGenerationTime) / 2;
    return tokenGenerationTime + buffer;
};

const refreshTokenState = async (): Promise<CachedMsalTokenState> => {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) {
        throw new Error('No active accounts found');
    }

    const account = accounts[0];

    let loginAuthenticationResult: AuthenticationResult | undefined;
    let customAuthenticationResult: AuthenticationResult | undefined;
    const tokenGenerationTime = Date.now();

    try {
        loginAuthenticationResult = await msalInstance.acquireTokenSilent({
            ...loginRequest,
            account,
        });
    } catch (error) {
        console.error('Error fetching login token:', error);
    }

    try {
        customAuthenticationResult = await msalInstance.acquireTokenSilent({
            ...customAPITokenRequest,
            account,
        });
    } catch (error) {
        console.error('Error fetching custom token:', error);
    }

    if (!loginAuthenticationResult || !customAuthenticationResult) {
        throw new Error('Failed to acquire tokens');
    }

    const tokenState: CachedMsalTokenState = {
        loginTokenCache: {
            authenticationResult: loginAuthenticationResult,
            halfExpiryTime: getHalfExpiryTime(
                tokenGenerationTime,
                loginAuthenticationResult.expiresOn
            ),
        },
        customTokenCache: {
            authenticationResult: customAuthenticationResult,
            halfExpiryTime: getHalfExpiryTime(
                tokenGenerationTime,
                customAuthenticationResult.expiresOn
            ),
        },
    };

    globalTokenState = tokenState;

    return tokenState;
};

const GetMSALResponse = async () => {
    if (globalTokenState && isGlobalTokenStateValid()) {
        return globalTokenState;
    }

    return refreshTokenState();
};

export default GetMSALResponse;
